import { DictionaryType } from 'helper/types';
import { DatasourceType, DatasourceStatuesType } from 'Services/api/v2';
import { getDictionaryByElementProperty } from 'helper';
import { SelectedBooleanStatusType, SelectedDataReceivedStatusType, SelectedStormModeStatusType } from '../utils';

/**
 * Фильтрация источников по его типу
 * @param datasources 
 * @param typeIds все возможные типы источников, которые могут быть в источниках
 * @returns 
 */
export function filterDatasourcesByDatasourceTypeIds(datasources: DatasourceType[], typeIds: number[] | undefined): DatasourceType[] {
  if (!typeIds || typeIds.length === 0) return datasources;
  return datasources.filter((datasource) => (
    typeIds.includes(datasource.datasourceTypeId)
  ));
}

type NameBooleanStatus = 'enableMode' | 'dangerMode' | 'softRelease' | 'dataReceived' | 'stormMode'
type SecondPropNameStatus = 'enabled' | 'isDanger' | 'isActual' | 'dataReceived' | 'vocModeId'
/**
 * Фильтрует источники по выбранному статусу
 * @param datasources 
 * @param datasourcesStatuses  словарь статусов по айди источника
 * @param status значение статуса по которому фильтруем все(`undefined`)/вкл/выкл/неизвестно(`0`)
 * @param statusName название объекта статуса, по которому фильтруем
 */
export function filterDatasourcesByStatus(
  datasources: DatasourceType[],
  datasourcesStatuses: DictionaryType<DatasourceStatuesType>,
  status: SelectedBooleanStatusType | SelectedDataReceivedStatusType | SelectedStormModeStatusType,
  statusName: NameBooleanStatus,
) {
  if (status === undefined) return datasources;

  // ищем название сво-ва для доставания нужного статуса в статусах источника
  const getSecondPropName = (): SecondPropNameStatus => {
    switch (statusName) {
      case 'enableMode':
        return 'enabled';
      case 'dangerMode':
        return 'isDanger';
      case 'softRelease':
        return 'isActual';
      case 'dataReceived':
        return 'dataReceived';
      case 'stormMode':
        return 'vocModeId';
      default:
        // так как в statusName ничего другого не может быть, то возвращаем результат последнего кейса,
        // чтобы ts потом не ругался
        return 'vocModeId';
    }
  };
  const secondPropName = getSecondPropName();

  return datasources.filter((datasource) => {
    // @ts-ignore так как пропсы ищем динамически
    const dsStatus: boolean | undefined | null = datasourcesStatuses[datasource.id]?.[statusName]?.[secondPropName];

    if (status === 0 && (dsStatus === undefined || dsStatus === null)) {
      return datasource;
    }
    if (status === dsStatus) return datasource;
    return false;
  });
}
// TODO: перенести в общие функции для словаря источников
export function getDatasourceIds(datasources: DatasourceType[] | DictionaryType<DatasourceType>): number[] {
  if (Array.isArray(datasources)) {
    return datasources.reduce((acc, datasource) => {
      acc.push(datasource.id);
      return acc;
    }, [] as number[]);
  }
  return Object.keys(datasources).reduce((acc, idString) => {
    acc.push(+idString);
    return acc;
  }, [] as number[]);
}

/**
 * собираем все возможные фильтры для источников для удобства использования  
 * @example 
 * const filtered = FilterDatasources.setValues(datasources).byDatasourceTypeIds([1,2,3,4]).getDictionary();
 */
export const FilterDatasources = {
  values: [] as DatasourceType[],
  setValues(datasources: DatasourceType[]) {
    this.values = datasources;
    return this;
  },

  byDatasourceTypeIds(typeIds: number[] | undefined) {
    this.values = filterDatasourcesByDatasourceTypeIds(this.values, typeIds);
    return this;
  },
  byEnabledMode(datasourcesStatuses: DictionaryType<DatasourceStatuesType>, enableMode: SelectedBooleanStatusType) {
    this.values = filterDatasourcesByStatus(this.values, datasourcesStatuses, enableMode, 'enableMode');
    return this;
  },
  byDangerMode(datasourcesStatuses: DictionaryType<DatasourceStatuesType>, dangerMode: SelectedBooleanStatusType) {
    this.values = filterDatasourcesByStatus(this.values, datasourcesStatuses, dangerMode, 'dangerMode');
    return this;
  },
  bySoftRelease(datasourcesStatuses: DictionaryType<DatasourceStatuesType>, softRelease: SelectedBooleanStatusType) {
    this.values = filterDatasourcesByStatus(this.values, datasourcesStatuses, softRelease, 'softRelease');
    return this;
  },
  byDataReceived(datasourcesStatuses: DictionaryType<DatasourceStatuesType>, dataReceived: SelectedDataReceivedStatusType) {
    this.values = filterDatasourcesByStatus(this.values, datasourcesStatuses, dataReceived, 'dataReceived');
    return this;
  },
  byStormMode(datasourcesStatuses: DictionaryType<DatasourceStatuesType>, stormMode: SelectedStormModeStatusType) {
    this.values = filterDatasourcesByStatus(this.values, datasourcesStatuses, stormMode, 'stormMode');
    return this;
  },

  getValues() {
    return this.values;
  },
  /** айди источников */
  getIds() {
    return getDatasourceIds(this.values);
  },
  /** по id источника */
  getDictionary() {
    return getDictionaryByElementProperty(this.values, 'id');
  },
};
